const ACCESS_TOKEN_KEY = 'wavelink_access_token';
const SESSION_EXPIRES_AT_KEY = 'wavelink_session_expires_at';

/** 旧版存储 Key，迁移后清除 */
const LEGACY_KEYS = ['token', 'refreshToken', 'tokenExpiresAt'] as const;

let memoryAccessToken: string | null = null;

function readPersist(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writePersist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`写入 localStorage(${key}) 失败:`, error);
  }
}

function removePersist(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function removeLegacySessionTokenKeys(): void {
  try {
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY);
  } catch {
    // ignore
  }
}

function notifyTokenChanged(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('auth-token-changed'));
  }
}

function clearLegacyLocalStorage(): void {
  try {
    for (const key of LEGACY_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    // ignore
  }
}

/**
 * 一次性将 sessionStorage 中的 Token 迁至 localStorage（关浏览器后仍可恢复登录）
 */
function migrateSessionStorageToLocalStorage(): void {
  try {
    const sessionToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    const sessionExpiresAt = sessionStorage.getItem(SESSION_EXPIRES_AT_KEY);

    if (sessionToken && !readPersist(ACCESS_TOKEN_KEY)) {
      writePersist(ACCESS_TOKEN_KEY, sessionToken);
    }
    if (sessionExpiresAt && !readPersist(SESSION_EXPIRES_AT_KEY)) {
      writePersist(SESSION_EXPIRES_AT_KEY, sessionExpiresAt);
    }

    if (sessionToken || sessionExpiresAt) {
      removeLegacySessionTokenKeys();
    }
  } catch (error) {
    console.warn('sessionStorage → localStorage Token 迁移失败:', error);
  }
}

/**
 * 迁移旧版 Token 存储：sessionStorage → localStorage，以及 legacy localStorage key
 */
export function migrateLegacyTokens(): void {
  migrateSessionStorageToLocalStorage();

  try {
    const legacyAccess = localStorage.getItem('token');
    const legacyExp = localStorage.getItem('tokenExpiresAt');

    if (legacyAccess && !readPersist(ACCESS_TOKEN_KEY)) {
      memoryAccessToken = legacyAccess;
      writePersist(ACCESS_TOKEN_KEY, legacyAccess);
    }
    if (legacyExp && !readPersist(SESSION_EXPIRES_AT_KEY)) {
      writePersist(SESSION_EXPIRES_AT_KEY, legacyExp);
    }

    if (legacyAccess || localStorage.getItem('refreshToken') || legacyExp) {
      clearLegacyLocalStorage();
    }
  } catch (error) {
    console.warn('Token 迁移失败:', error);
  }
}

export const secureTokenStorage = {
  getAccessToken(): string | null {
    if (memoryAccessToken) {
      return memoryAccessToken;
    }
    const stored = readPersist(ACCESS_TOKEN_KEY);
    if (stored) {
      memoryAccessToken = stored;
    }
    return stored;
  },

  getSessionExpiresAt(): number | null {
    const stored = readPersist(SESSION_EXPIRES_AT_KEY);
    if (!stored) {
      return null;
    }
    const parsed = parseInt(stored, 10);
    return Number.isNaN(parsed) ? null : parsed;
  },

  setAccessToken(accessToken: string, expiresInSeconds?: number): void {
    memoryAccessToken = accessToken;
    writePersist(ACCESS_TOKEN_KEY, accessToken);
    removeLegacySessionTokenKeys();

    if (expiresInSeconds && expiresInSeconds > 0) {
      writePersist(SESSION_EXPIRES_AT_KEY, String(Date.now() + expiresInSeconds * 1000));
    }

    clearLegacyLocalStorage();
    notifyTokenChanged();
  },

  clearTokens(): void {
    memoryAccessToken = null;
    removePersist(ACCESS_TOKEN_KEY);
    removePersist(SESSION_EXPIRES_AT_KEY);
    removeLegacySessionTokenKeys();
    clearLegacyLocalStorage();
    notifyTokenChanged();
  },

  hasAccessToken(): boolean {
    return Boolean(this.getAccessToken());
  },
};
